import React, { useState, useEffect, useRef } from 'react';
import {
    FaTimes,
    FaStickyNote,
    FaPlus,
    FaEdit,
    FaTrash,
    FaExclamationTriangle,
    FaCalendarAlt,
    FaFilter,
    FaCheckCircle,
    FaBell,
    FaBellSlash,
    FaClock
} from 'react-icons/fa';
import styles from './NotesModal.module.css';
import { noteService, NoteHelper, OwnerType } from '../../services/noteService';
import { useAccessToken, useLockBodyScroll } from '../../hooks';
import { useAlert } from '../../context/AlertContext';
import { useNotePriorities } from '../../hooks/useNotePriorities';
import { useNoteTypes } from '../../hooks/useNoteTypes';

/**
 * Note Card Component
 * Displays individual note information in a card format
 */
const NoteCard = ({ note, onEdit, onDelete, onToggleAlert, onExtend }) => {
    const status = NoteHelper.getNoteStatus(note);
    const formattedDate = NoteHelper.formatDate(note.createdAt);
    const effectiveDate = NoteHelper.formatDate(note.effectiveFrom);
    const expirationDate = note.expiration ? NoteHelper.formatDate(note.expiration) : null;

    return (
        <div className={`${styles.noteCard} ${styles[status]} ${note.isAlert ? styles.alert : ''}`}>
            {/* Header */}
            <div className={styles.noteHeader}>
                <div className={styles.noteTitle}>
                    {note.isAlert && <FaBell className={styles.alertIcon} />}
                    <h4>{note.title}</h4>
                </div>
                <div className={styles.noteBadges}>
                    <span className={`${styles.badge} ${styles.type}`}>
                        {note.noteTypeName || `Type ${note.noteTypeId}`}
                    </span>
                </div>
            </div>

            {/* Body */}
            <div className={styles.noteBody}>
                <p>{note.body}</p>
            </div>
                
            {/* Footer */}
            <div className={styles.noteFooter}>
                <div className={styles.noteDates}>
                    <div className={styles.dateItem}>
                        <FaCalendarAlt className={styles.dateIcon} />
                        <span className={styles.dateLabel}>Effective:</span>
                        <span>{effectiveDate}</span>
                    </div>
                    {expirationDate && (
                        <div className={styles.dateItem}>
                            <FaClock className={styles.dateIcon} />
                            <span className={styles.dateLabel}>Expires:</span>
                            <span>{expirationDate}</span>
                        </div>
                    )}
                </div>
                <div className={styles.noteMeta}>
                    <small>Created: {formattedDate}</small>
                </div>
            </div>

            {/* Actions */}
            <div className={styles.noteActions}>
                <button
                    className={styles.btnEdit}
                    onClick={() => onEdit(note)}
                    title="Edit note"
                >
                    <FaEdit />
                </button>
                {note.expiration && status !== 'expired' && (
                    <button
                        className={styles.btnExtend}
                        onClick={() => onExtend(note)}
                        title="Extend expiration"
                    >
                        <FaClock />
                    </button>
                )}
                <button
                    className={styles.btnDelete}
                    onClick={() => onDelete(note)}
                    title="Delete note"
                >
                    <FaTrash />
                </button>
            </div>
        </div>
    );
};

/**
 * Note Form Component
 * Form for creating/editing notes
 */
const NoteForm = ({ 
    note, 
    ownerType, 
    ownerId, 
    priorities, 
    noteTypes, 
    onSubmit, 
    onCancel,
    saving 
}) => {
    const [formData, setFormData] = useState({
        title: '',
        body: '',
        isAlert: false,
        priorityId: '',
        noteTypeId: '',
        effectiveFrom: NoteHelper.getCurrentDateTimeForInput(),
        expiration: ''
    });

    const [errors, setErrors] = useState({});
    const isEditMode = !!note;

    // Initialize form with note data if editing
    useEffect(() => {
        if (note) {
            setFormData({
                title: note.title || '',
                body: note.body || '',
                isAlert: note.isAlert || false,
                priorityId: note.priorityId || '',
                noteTypeId: note.noteTypeId || '',
                effectiveFrom: note.effectiveFrom ? NoteHelper.formatDateForInput(note.effectiveFrom) : NoteHelper.getCurrentDateTimeForInput(),
                expiration: note.expiration ? NoteHelper.formatDateForInput(note.expiration) : ''
            });
        }
    }, [note]);

    // Set default priority and note type if not editing
    useEffect(() => {
        if (!note && priorities.length > 0 && !formData.priorityId) {
            setFormData(prev => ({ ...prev, priorityId: priorities[0].priorityId }));
        }
        if (!note && noteTypes.length > 0 && !formData.noteTypeId) {
            setFormData(prev => ({ ...prev, noteTypeId: noteTypes[0].noteTypeId }));
        }
    }, [note, priorities, noteTypes, formData.priorityId, formData.noteTypeId]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));

        // Clear field error
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: null }));
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setErrors({});

        // Prepare data for validation and submission
        // Convert local datetime to UTC for backend
        const noteData = {
            ...formData,
            priorityId: parseInt(formData.priorityId),
            noteTypeId: parseInt(formData.noteTypeId),
            ownerType,
            ownerId: parseInt(ownerId),
            effectiveFrom: NoteHelper.convertLocalToUTC(formData.effectiveFrom),
            expiration: formData.expiration ? NoteHelper.convertLocalToUTC(formData.expiration) : null
        };

        // Validate
        const validation = NoteHelper.validateNote(noteData);
        if (!validation.valid) {
            const fieldErrors = {};
            validation.errors.forEach(error => {
                if (error.includes('Title')) fieldErrors.title = error;
                else if (error.includes('Body')) fieldErrors.body = error;
                else if (error.includes('Priority')) fieldErrors.priorityId = error;
                else if (error.includes('Note type')) fieldErrors.noteTypeId = error;
                else if (error.includes('Effective')) fieldErrors.effectiveFrom = error;
                else if (error.includes('Expiration')) fieldErrors.expiration = error;
            });
            setErrors(fieldErrors);
            return;
        }

        onSubmit(noteData);
    };

    return (
        <form onSubmit={handleSubmit} className={styles.noteForm}>
            <h3>{isEditMode ? 'Edit Note' : 'New Note'}</h3>

            {/* Title */}
            <div className={styles.formGroup}>
                <label htmlFor="title" className={styles.label}>
                    Title <span className={styles.required}>*</span>
                </label>
                <input
                    type="text"
                    id="title"
                    name="title"
                    className={`${styles.input} ${errors.title ? styles.inputError : ''}`}
                    value={formData.title}
                    onChange={handleChange}
                    maxLength={200}
                    disabled={saving}
                    required
                    placeholder="Enter note title..."
                />
                {errors.title && <span className={styles.errorMessage}>{errors.title}</span>}
                <span className={styles.charCount}>{formData.title.length}/200</span>
            </div>

            {/* Body */}
            <div className={styles.formGroup}>
                <label htmlFor="body" className={styles.label}>
                    Body <span className={styles.required}>*</span>
                </label>
                <textarea
                    id="body"
                    name="body"
                    className={`${styles.textarea} ${errors.body ? styles.inputError : ''}`}
                    value={formData.body}
                    onChange={handleChange}
                    maxLength={4000}
                    disabled={saving}
                    required
                    rows={6}
                    placeholder="Enter note content..."
                />
                {errors.body && <span className={styles.errorMessage}>{errors.body}</span>}
                <span className={styles.charCount}>{formData.body.length}/4000</span>
            </div>

            {/* Priority and Note Type */}
            <div className={styles.formRow}>
                <div className={styles.formGroup}>
                    <label htmlFor="priorityId" className={styles.label}>
                        Priority <span className={styles.required}>*</span>
                    </label>
                    <select
                        id="priorityId"
                        name="priorityId"
                        className={`${styles.select} ${errors.priorityId ? styles.inputError : ''}`}
                        value={formData.priorityId}
                        onChange={handleChange}
                        disabled={saving}
                        required
                    >
                        <option value="">Select Priority</option>
                        {priorities.map(priority => (
                            <option key={priority.priorityId} value={priority.priorityId}>
                                {priority.priorityName} (Weight: {priority.priorityWeight})
                            </option>
                        ))}
                    </select>
                    {errors.priorityId && <span className={styles.errorMessage}>{errors.priorityId}</span>}
                </div>

                <div className={styles.formGroup}>
                    <label htmlFor="noteTypeId" className={styles.label}>
                        Note Type <span className={styles.required}>*</span>
                    </label>
                    <select
                        id="noteTypeId"
                        name="noteTypeId"
                        className={`${styles.select} ${errors.noteTypeId ? styles.inputError : ''}`}
                        value={formData.noteTypeId}
                        onChange={handleChange}
                        disabled={saving}
                        required
                    >
                        <option value="">Select Type</option>
                        {noteTypes.map(type => (
                            <option key={type.noteTypeId} value={type.noteTypeId}>
                                {type.noteTypeName}
                            </option>
                        ))}
                    </select>
                    {errors.noteTypeId && <span className={styles.errorMessage}>{errors.noteTypeId}</span>}
                </div>
            </div>

            {/* Effective From and Expiration */}
            <div className={styles.formRow}>
                <div className={styles.formGroup}>
                    <label htmlFor="effectiveFrom" className={styles.label}>
                        Effective From <span className={styles.required}>*</span>
                    </label>
                    <input
                        type="datetime-local"
                        id="effectiveFrom"
                        name="effectiveFrom"
                        className={`${styles.input} ${errors.effectiveFrom ? styles.inputError : ''}`}
                        value={formData.effectiveFrom}
                        onChange={handleChange}
                        disabled={saving}
                        required
                    />
                    {errors.effectiveFrom && <span className={styles.errorMessage}>{errors.effectiveFrom}</span>}
                </div>

                <div className={styles.formGroup}>
                    <label htmlFor="expiration" className={styles.label}>
                        Expiration (Optional)
                    </label>
                    <input
                        type="datetime-local"
                        id="expiration"
                        name="expiration"
                        className={`${styles.input} ${errors.expiration ? styles.inputError : ''}`}
                        value={formData.expiration}
                        onChange={handleChange}
                        disabled={saving}
                    />
                    {errors.expiration && <span className={styles.errorMessage}>{errors.expiration}</span>}
                </div>
            </div>

            {/* Is Alert */}
            <div className={styles.checkboxGroup}>
                <label className={styles.checkboxLabel}>
                    <input
                        type="checkbox"
                        name="isAlert"
                        checked={formData.isAlert}
                        onChange={handleChange}
                        disabled={saving}
                    />
                    <span>Mark as Alert (Important)</span>
                </label>
            </div>

            {/* Form Actions */}
            <div className={styles.formActions}>
                <button
                    type="button"
                    className={styles.btnSecondary}
                    onClick={onCancel}
                    disabled={saving}
                >
                    Cancel
                </button>
                <button
                    type="submit"
                    className={styles.btnPrimary}
                    disabled={saving}
                >
                    {saving ? (
                        <>
                            <span className={styles.spinner}></span>
                            Saving...
                        </>
                    ) : (
                        <>
                            <FaCheckCircle /> {isEditMode ? 'Update Note' : 'Create Note'}
                        </>
                    )}
                </button>
            </div>
        </form>
    );
};

/**
 * Delete Confirmation Dialog
 */
const DeleteConfirmDialog = ({ note, isOpen, onClose, onConfirm, deleting }) => {
    if (!isOpen || !note) return null;

    return (
        <div className={styles.dialogOverlay} onClick={onClose}>
            <div className={styles.dialogContent} onClick={(e) => e.stopPropagation()}>
                <div className={styles.dialogHeader}>
                    <h3>Delete Note</h3>
                    <button
                        className={styles.closeButton}
                        onClick={onClose}
                        disabled={deleting}
                    >
                        <FaTimes />
                    </button>
                </div>
                <div className={styles.dialogBody}>
                    <div className={styles.warningIcon}>
                        <FaExclamationTriangle size={48} color="#e74c3c" />
                    </div>
                    <p className={styles.confirmMessage}>
                        Are you sure you want to delete this note? This action cannot be undone.
                    </p>
                    <div className={styles.notePreview}>
                        <p><strong>Title:</strong> {note.title}</p>
                        <p><strong>Created:</strong> {NoteHelper.formatDate(note.createdAt)}</p>
                    </div>
                </div>
                <div className={styles.dialogActions}>
                    <button
                        type="button"
                        className={styles.btnSecondary}
                        onClick={onClose}
                        disabled={deleting}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        className={styles.btnDanger}
                        onClick={onConfirm}
                        disabled={deleting}
                    >
                        {deleting ? 'Deleting...' : 'Delete Note'}
                    </button>
                </div>
            </div>
        </div>
    );
};

/**
 * Notes Modal Component
 * Reusable modal for managing notes for any entity (Client, Sample, Contact, etc.)
 */
const NotesModal = ({ 
    ownerType, 
    ownerId, 
    ownerName, 
    ownerCode,
    isOpen, 
    onClose,
    onNotesChanged 
}) => {
    const { getBackendAccessToken } = useAccessToken();
    const { setInfoAlert, setErrorAlert, setIsModalOpen } = useAlert();
    const { priorities, fetchPriorities } = useNotePriorities({ autoFetch: false });
    const { noteTypes, fetchNoteTypes } = useNoteTypes({ autoFetch: false });

    // Lock body scroll when modal is open
    useLockBodyScroll(isOpen);

    // State management
    const [notes, setNotes] = useState([]);
    const [filteredNotes, setFilteredNotes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [filter, setFilter] = useState('all'); // 'all', 'active', 'alerts', 'expired', 'scheduled'
    const [showForm, setShowForm] = useState(false);
    const [editingNote, setEditingNote] = useState(null);
    const [deletingNote, setDeletingNote] = useState(null);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);

    const prevIsOpenRef = useRef(false);

    /**
     * Load notes for the owner
     */
    const loadNotes = async () => {
        if (!ownerId || !ownerType) return;

        try {
            setLoading(true);
            const accessToken = await getBackendAccessToken();

            // Load notes and note types in parallel
            const [data, noteTypesData, prioritiesData] = await Promise.all([
                noteService.getByOwner(accessToken, ownerType, ownerId),
                noteTypes.length > 0 ? Promise.resolve(noteTypes) : fetchNoteTypes(),
                priorities.length > 0 ? Promise.resolve(priorities) : fetchPriorities()
            ]);
            
            // Enrich notes with note type names, priority names, and priority weights if not present
            const enrichedNotes = data.map(note => {
                const enriched = { ...note };
                
                // Add note type name if missing
                if (!enriched.noteTypeName && enriched.noteTypeId) {
                    const noteType = noteTypesData.find(nt => nt.noteTypeId === enriched.noteTypeId);
                    if (noteType) {
                        enriched.noteTypeName = noteType.noteType;
                    }
                }
                
                // Add priority name and weight if missing
                if (enriched.priorityId) {
                    const priority = prioritiesData.find(p => p.priorityId === enriched.priorityId);
                    if (priority) {
                        if (!enriched.priorityName) {
                            enriched.priorityName = priority.priorityName;
                        }
                        if (!enriched.priorityWeight) {
                            enriched.priorityWeight = priority.priorityWeight;
                        }
                    }
                }
                
                return enriched;
            });
            
            // Sort by priority
            const sorted = NoteHelper.sortByPriority(enrichedNotes);
            setNotes(sorted);
        } catch (error) {
            console.error('Failed to load notes:', error);
            setErrorAlert('Failed to load notes. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    /**
     * Filter and sort notes - always maintain priority sort order
     */
    useEffect(() => {
        const filtered = NoteHelper.filterByStatus(notes, filter);
        // Ensure priority sort is maintained after filtering
        const sorted = NoteHelper.sortByPriority(filtered);
        setFilteredNotes(sorted);
    }, [notes, filter]);

    /**
     * Load notes when modal opens
     */
    useEffect(() => {
        const wasClosedNowOpen = !prevIsOpenRef.current && isOpen;

        if (wasClosedNowOpen && ownerId && ownerType) {
            loadNotes();
            // Load priorities and note types if not already loaded
            if (priorities.length === 0) fetchPriorities();
            if (noteTypes.length === 0) fetchNoteTypes();
        }

        prevIsOpenRef.current = isOpen;
    }, [isOpen, ownerId, ownerType]);

    /**
     * Manage modal state in context
     */
    useEffect(() => {
        if (isOpen) {
            document.body.setAttribute('data-modal-open', 'true');
            setIsModalOpen(true);
        } else {
            document.body.removeAttribute('data-modal-open');
            setIsModalOpen(false);
        }

        return () => {
            document.body.removeAttribute('data-modal-open');
            setIsModalOpen(false);
        };
    }, [isOpen, setIsModalOpen]);

    /**
     * Handle form submit (create or update)
     */
    const handleFormSubmit = async (noteData) => {
        setSaving(true);

        try {
            const accessToken = await getBackendAccessToken();

            if (editingNote) {
                // Update existing note
                const updated = await noteService.update(accessToken, editingNote.noteId, noteData);
                setNotes(prev => prev.map(n => n.noteId === editingNote.noteId ? updated : n));
                setInfoAlert('Note updated successfully!');
            } else {
                // Create new note
                const created = await noteService.create(accessToken, noteData);
                setNotes(prev => [created, ...prev]);
                setInfoAlert('Note created successfully!');
            }

            // Close form
            setShowForm(false);
            setEditingNote(null);

            // Notify parent
            if (onNotesChanged) {
                onNotesChanged();
            }
        } catch (error) {
            console.error('Failed to save note:', error);
            setErrorAlert(`Failed to save note: ${error.message || 'Unknown error'}`);
        } finally {
            setSaving(false);
        }
    };

    /**
     * Handle edit note
     */
    const handleEdit = (note) => {
        setEditingNote(note);
        setShowForm(true);
    };

    /**
     * Handle delete note
     */
    const handleDelete = (note) => {
        setDeletingNote(note);
        setShowDeleteDialog(true);
    };

    /**
     * Confirm delete
     */
    const handleConfirmDelete = async () => {
        if (!deletingNote) return;

        setSaving(true);

        try {
            const accessToken = await getBackendAccessToken();
            await noteService.delete(accessToken, deletingNote.noteId);
            
            setNotes(prev => prev.filter(n => n.noteId !== deletingNote.noteId));
            setInfoAlert('Note deleted successfully!');
            setShowDeleteDialog(false);
            setDeletingNote(null);

            // Notify parent
            if (onNotesChanged) {
                onNotesChanged();
            }
        } catch (error) {
            console.error('Failed to delete note:', error);
            setErrorAlert(`Failed to delete note: ${error.message || 'Unknown error'}`);
        } finally {
            setSaving(false);
        }
    };

    /**
     * Handle toggle alert
     */
    const handleToggleAlert = async (note) => {
        try {
            const accessToken = await getBackendAccessToken();
            await noteService.setAlert(accessToken, note.noteId, !note.isAlert);
            
            setNotes(prev => prev.map(n => 
                n.noteId === note.noteId ? { ...n, isAlert: !n.isAlert } : n
            ));
            setInfoAlert(`Alert ${!note.isAlert ? 'enabled' : 'disabled'} for note`);
        } catch (error) {
            console.error('Failed to toggle alert:', error);
            setErrorAlert('Failed to toggle alert. Please try again.');
        }
    };

    /**
     * Handle extend expiration
     */
    const handleExtend = async (note) => {
        const days = prompt('Extend by how many days?', '7');
        if (!days) return;

        try {
            const accessToken = await getBackendAccessToken();
            const updated = await noteService.extendExpiration(accessToken, note.noteId, parseInt(days));
            
            setNotes(prev => prev.map(n => n.noteId === note.noteId ? updated : n));
            setInfoAlert(`Note expiration extended by ${days} days`);
        } catch (error) {
            console.error('Failed to extend note:', error);
            setErrorAlert('Failed to extend note. Please try again.');
        }
    };

    /**
     * Handle add new note
     */
    const handleAddNew = () => {
        setEditingNote(null);
        setShowForm(true);
    };

    /**
     * Handle cancel form
     */
    const handleCancelForm = () => {
        setShowForm(false);
        setEditingNote(null);
    };

    // Don't render if not open
    if (!isOpen) return null;

    return (
        <div className={styles.modalOverlay}>
            <div className={styles.modalContent}>
                {/* Modal Header */}
                <div className={styles.modalHeader}>
                    <div className={styles.headerInfo}>
                        <FaStickyNote className={styles.headerIcon} />
                        <div>
                            <h2 className={styles.modalTitle}>Notes</h2>
                            <p className={styles.modalSubtitle}>
                                {ownerCode ? `${ownerCode} - ${ownerName}` : ownerName}
                            </p>
                        </div>
                    </div>
                    <button
                        className={styles.closeButton}
                        onClick={onClose}
                        title="Close"
                    >
                        <FaTimes />
                    </button>
                </div>

                {/* Form or List */}
                {showForm ? (
                    <div className={styles.formContainer}>
                        <NoteForm
                            key={editingNote?.noteId || 'new'}
                            note={editingNote}
                            ownerType={ownerType}
                            ownerId={ownerId}
                            priorities={priorities}
                            noteTypes={noteTypes}
                            onSubmit={handleFormSubmit}
                            onCancel={handleCancelForm}
                            saving={saving}
                        />
                    </div>
                ) : (
                    <>
                        {/* Controls Section */}
                        <div className={styles.controlsSection}>
                            <button
                                className={styles.addButton}
                                onClick={handleAddNew}
                                title="Add new note"
                            >
                                <FaPlus /> Add Note
                            </button>

                            {/* Filter Controls */}
                            <div className={styles.filterControls}>
                                <FaFilter className={styles.filterIcon} />
                                <button
                                    className={`${styles.filterButton} ${filter === 'all' ? styles.active : ''}`}
                                    onClick={() => setFilter('all')}
                                >
                                    All ({notes.length})
                                </button>
                                <button
                                    className={`${styles.filterButton} ${filter === 'active' ? styles.active : ''}`}
                                    onClick={() => setFilter('active')}
                                >
                                    Active ({notes.filter(n => NoteHelper.isNoteActive(n)).length})
                                </button>
                                <button
                                    className={`${styles.filterButton} ${filter === 'alerts' ? styles.active : ''}`}
                                    onClick={() => setFilter('alerts')}
                                >
                                    Alerts ({notes.filter(n => n.isAlert).length})
                                </button>
                            </div>
                        </div>

                        {/* Notes List */}
                        <div className={styles.notesList}>
                            {loading ? (
                                <div className={styles.loadingMessage}>
                                    <div className={styles.spinner}></div>
                                    <p>Loading notes...</p>
                                </div>
                            ) : filteredNotes.length === 0 ? (
                                <div className={styles.emptyState}>
                                    <FaStickyNote className={styles.emptyIcon} />
                                    <p>
                                        {filter === 'all' 
                                            ? 'No notes yet. Click "Add Note" to create one.'
                                            : `No ${filter} notes found.`
                                        }
                                    </p>
                                </div>
                            ) : (
                                filteredNotes.map(note => (
                                    <NoteCard
                                        key={note.noteId}
                                        note={note}
                                        onEdit={handleEdit}
                                        onDelete={handleDelete}
                                        onToggleAlert={handleToggleAlert}
                                        onExtend={handleExtend}
                                    />
                                ))
                            )}
                        </div>
                    </>
                )}

                {/* Delete Confirmation Dialog */}
                <DeleteConfirmDialog
                    note={deletingNote}
                    isOpen={showDeleteDialog}
                    onClose={() => {
                        setShowDeleteDialog(false);
                        setDeletingNote(null);
                    }}
                    onConfirm={handleConfirmDelete}
                    deleting={saving}
                />
            </div>
        </div>
    );
};

export default NotesModal;
